// src/pages/Home/container/StudioAndCommunityBand.tsx
import { Link } from 'react-router-dom';
import { ArrowRight, Clapperboard, Megaphone, Users } from 'lucide-react';
import MemberCard from '../../../components/Studio/MemberCard';
import ProductionCard from '../../../components/Studio/ProductionCard';
import {
  BRAND_GRADIENT_TEXT,
  CONTAINER,
  EYEBROW,
  GHOST_BUTTON,
  PANEL,
  PRIMARY_BUTTON,
  SECONDARY_BUTTON,
} from '../../../components/Studio/studioStyles';
import useStudioResource from '../../../hooks/useStudioResource';
import communityService from '../../../services/communityService';
import productionService from '../../../services/productionService';

/**
 * The homepage band that says what StrymHub actually is: a production company with a
 * community, not a catalogue. Sits between the content rows and the closing showcase.
 *
 * Every block degrades to a recruitment CTA when there's no data yet, so an empty
 * platform reads as "early" rather than "abandoned".
 */
const StudioAndCommunityBand = () => {
  const stats = useStudioResource((signal) => communityService.getStats(signal), []);
  const productions = useStudioResource(
    (signal) => productionService.getProductions({ size: 4 }, signal),
    [],
  );
  const members = useStudioResource(
    (signal) => communityService.getMembers({ size: 4 }, signal),
    [],
  );

  const productionList = productions.data?.content ?? [];
  const memberList = members.data?.content ?? [];

  return (
    <section className="border-y border-white/10 bg-[#0B0B0D] py-20 sm:py-24">
      <div className={CONTAINER}>
        {/* The statement */}
        <div className="max-w-3xl">
          <p className={EYEBROW}>StrymHub Studios · Sheffield, UK</p>
          <h2 className="heading-cinematic mt-4 text-3xl text-white sm:text-4xl lg:text-5xl">
            We&apos;re not a catalogue.{' '}
            <span className={BRAND_GRADIENT_TEXT}>We&apos;re a studio with a community.</span>
          </h2>
          <p className="mt-6 text-base leading-relaxed text-neutral-400 sm:text-lg">
            Everything here was written, cast, shot and finished by StrymHub members — people who
            joined the troupe, got credited by name, and built a body of work in public. When we
            cast our next production, we look inside this community first.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/community/join" className={PRIMARY_BUTTON}>
              Join the troupe
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <Link to="/studios" className={SECONDARY_BUTTON}>
              How the studio works
            </Link>
          </div>
        </div>

        {/* Three doors */}
        <div className="mt-16 grid gap-4 md:grid-cols-3">
          <Link
            to="/productions"
            className={`${PANEL} group p-6 transition-colors hover:border-orange-500/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
          >
            <Clapperboard className="h-6 w-6 text-orange-400" aria-hidden="true" />
            <h3 className="mt-4 font-display text-lg font-bold text-white">Productions</h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Every film, series and podcast grouped by the production it came from — with the full
              cast and crew list attached.
            </p>
            {stats.data && (
              <p className="mt-4 font-display text-2xl font-bold text-white">
                {stats.data.productions}
                <span className="ml-2 text-sm font-normal text-neutral-500">on the slate</span>
              </p>
            )}
          </Link>

          <Link
            to="/community/troupe"
            className={`${PANEL} group p-6 transition-colors hover:border-orange-500/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
          >
            <Users className="h-6 w-6 text-orange-400" aria-hidden="true" />
            <h3 className="mt-4 font-display text-lg font-bold text-white">The Troupe</h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Actors, directors, writers, camera, sound, edit, music, design. Everyone has their own
              page and their own credits.
            </p>
            {stats.data && (
              <p className="mt-4 font-display text-2xl font-bold text-white">
                {stats.data.members}
                <span className="ml-2 text-sm font-normal text-neutral-500">members</span>
              </p>
            )}
          </Link>

          <Link
            to="/casting"
            className={`${PANEL} group p-6 transition-colors hover:border-orange-500/40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-400`}
          >
            <Megaphone className="h-6 w-6 text-orange-400" aria-hidden="true" />
            <h3 className="mt-4 font-display text-lg font-bold text-white">Open Calls</h3>
            <p className="mt-2 text-sm leading-relaxed text-neutral-400">
              Roles in front of camera and positions behind it. Members apply directly — no agent,
              no gatekeeper.
            </p>
            {stats.data && (
              <p className="mt-4 font-display text-2xl font-bold text-white">
                {stats.data.openCalls}
                <span className="ml-2 text-sm font-normal text-neutral-500">open now</span>
              </p>
            )}
          </Link>
        </div>

        {/* Productions strip */}
        {productionList.length > 0 && (
          <div className="mt-20">
            <div className="flex items-end justify-between gap-4">
              <h3 className="heading-cinematic text-2xl text-white">On the slate</h3>
              <Link to="/productions" className={GHOST_BUTTON}>
                All productions
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {productionList.map((production) => (
                <ProductionCard key={production.id} production={production} />
              ))}
            </div>
          </div>
        )}

        {/* Members strip */}
        {memberList.length > 0 && (
          <div className="mt-20">
            <div className="flex items-end justify-between gap-4">
              <h3 className="heading-cinematic text-2xl text-white">The people who make it</h3>
              <Link to="/community/troupe" className={GHOST_BUTTON}>
                Meet the troupe
                <ArrowRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
              {memberList.map((member) => (
                <MemberCard key={member.id} member={member} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default StudioAndCommunityBand;
